/**
 * <TierPill> — compact, colour-coded badge showing the user's subscription tier.
 *
 * Three states:
 *   Ember (free)    — neutral amber/ember tone
 *   Steel (pro)     — slate gradient
 *   Damascus (pro_plus) — blue → violet gradient
 *
 * Used next to the user's name in the navbar so the tier they're on is
 * always visible without surfacing it as a separate piece of UI.
 *
 * Renders nothing when there's no user yet (still loading) so it doesn't
 * pop in mid-mount.
 */
import React from 'react';
import { Crown } from 'lucide-react';
import { effectiveTier, isOwner, TIER_LABEL } from '../lib/tier';

const TIER_STYLE = {
  free: {
    backgroundColor: 'rgba(245, 158, 11, 0.18)',  // amber-500 @ 18%
    color: '#FCD34D',                              // amber-300
    border: '1px solid rgba(245, 158, 11, 0.35)',
  },
  pro: {
    backgroundImage: 'linear-gradient(135deg, #64748B 0%, #0F172A 100%)',
    color: '#FFFFFF',
    border: '1px solid rgba(255, 255, 255, 0.1)',
  },
  pro_plus: {
    backgroundImage: 'linear-gradient(135deg, #1E40AF 0%, #7C3AED 100%)',
    color: '#FFFFFF',
    border: '1px solid rgba(255, 255, 255, 0.12)',
  },
  owner: {
    backgroundImage: 'linear-gradient(135deg, #B45309 0%, #F59E0B 50%, #FCD34D 100%)',
    color: '#1A1206',
    border: '1px solid rgba(245, 158, 11, 0.55)',
    boxShadow: '0 0 0 1px rgba(252, 211, 77, 0.25)',
  },
};

const TierPill = ({ user, className = '' }) => {
  if (!user) return null;
  if (isOwner(user)) {
    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-wider leading-none ${className}`}
        style={TIER_STYLE.owner}
        data-testid="tier-pill-owner"
        title="Forge Ledger Owner — uneditable, protected account"
      >
        <Crown className="w-3 h-3" />
        Owner
      </span>
    );
  }
  const tier = effectiveTier(user);
  const label = TIER_LABEL[tier] || 'Ember';
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider leading-none ${className}`}
      style={TIER_STYLE[tier] || TIER_STYLE.free}
      data-testid={`tier-pill-${tier}`}
      title={`Forge Ledger ${label}`}
    >
      {label}
    </span>
  );
};

export default TierPill;
